import { useQuery } from 'react-query';

import type { QueryFunction } from '@frontend/api';
import { useRequest } from '@frontend/api';

type Subquote = {
  id: number;
  site: {
    id: number;
    name: string;
    businessName: string;
  };
  quoteNumber: number;
  requestedOn: string;
  finalizedOn: string | null;
  note: string | null;
  sender: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
  userCanAccess: boolean;
};

type ComponentQuantity = {
  id: number;
  leadTime: number;
  leadTimeDisplayUnit: string;
  quantity: number;
  totalPriceWithAllAddOns: string;
  totalUnitAddOns: string;
  totalUnitCost: string;
  totalUnitMarkup: string;
  totalUnitPriceWithAllAddOns: string;
};

export type UseListSubcomponentsParams = {
  componentId: number;
};

export type ListSubcomponentsData = {
  id: number;
  quote: Subquote;
  quantities: ComponentQuantity[] | null;
};

export type ListSubcomponentsQueryKeyType = readonly [
  'components',
  number,
  'listSubcomponents',
];

export const ListSubcomponentsQueryKey = (
  componentId: number,
): ListSubcomponentsQueryKeyType => {
  return ['components', componentId, 'listSubcomponents'];
};

export const useListSubcomponents: QueryFunction<
  ListSubcomponentsData[],
  [UseListSubcomponentsParams],
  ListSubcomponentsQueryKeyType
> = (params, { requestOptions, queryOptions } = {}) => {
  const { componentId } = params;
  const request = useRequest(requestOptions);
  const queryKey = ListSubcomponentsQueryKey(componentId);

  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: ({ signal }) => {
      return request(`/quotes/components/${componentId}/subcomponents`, {
        init: {
          method: 'GET',
          signal,
        },
      });
    },
  });
};
